import ExcelJS from 'exceljs';
import { downloadTemplate } from './templateDownloader.js';
import { 
  mapHoursOfOperation, 
  mapDialedNumberMappings, 
  mapAgentStatus, 
  mapQueues, 
  mapQuickConnects, 
  mapRoutingProfiles,
  mapUserHierarchyData,
  mapSecurityProfiles,
  mapLambdaFunctions,
  mapLexBots,
  mapLexIntents,
  mapLexSlotsAndTypes,
  mapConnectLambdaFunctions,
  mapContactFlows,
  mapContactFlowModules,
  mapInstanceConfiguration,
  mapUsers
} from './dataMappers.js';
import { 
  SHEET_NAMES, 
  COLUMN_MAPPINGS, 
  DATA_START_ROW, 
  HIERARCHY_STRUCTURE_ROW,
  DEFAULT_SECURITY_PROFILES,
  getColumnLetter,
  INSTANCE_CONFIG_FIELDS
} from './constants.js';

export class ExcelExporter {
  constructor() {
    this.workbook = null;
  }

  async initialize() {
    try {
      const templateData = await downloadTemplate();
      this.workbook = new ExcelJS.Workbook();
      await this.workbook.xlsx.load(templateData);
      return true;
    } catch (error) {
      console.error('Failed to initialize Excel exporter:', error);
      throw error;
    }
  }

  populateHoursOfOperation(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.HOURS_OF_OPERATION);
    if (!worksheet) {
      throw new Error(`Sheet "${SHEET_NAMES.HOURS_OF_OPERATION}" not found in template`);
    }

    // Map the data to the format needed for Excel
    const mappedData = mapHoursOfOperation(exportedData);
    
    // Populate the worksheet
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.HOURS_OF_OPERATION + index;
      
      // Get the row to preserve any existing formatting
      const worksheetRow = worksheet.getRow(rowNum);
      
      // Set cell values while preserving formatting
      worksheetRow.getCell(COLUMN_MAPPINGS.HOURS_OF_OPERATION.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.HOURS_OF_OPERATION.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.HOURS_OF_OPERATION.HOURS_OF_OPERATION).value = row.hoursOfOperation;
      worksheetRow.getCell(COLUMN_MAPPINGS.HOURS_OF_OPERATION.TIME_ZONE).value = row.timeZone;
      
      // Commit the row to apply changes
      worksheetRow.commit();
    });

  }

  populateDialedNumberMappings(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.DIALED_NUMBER_MAPPINGS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.DIALED_NUMBER_MAPPINGS}" not found in template`);
      return;
    }

    const mappedData = mapDialedNumberMappings(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.DIALED_NUMBER_MAPPINGS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.DIALED_NUMBER_MAPPINGS.PHONE_NUMBER).value = row.phoneNumber;
      worksheetRow.getCell(COLUMN_MAPPINGS.DIALED_NUMBER_MAPPINGS.FORWARDED_FROM).value = '';
      worksheetRow.getCell(COLUMN_MAPPINGS.DIALED_NUMBER_MAPPINGS.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.DIALED_NUMBER_MAPPINGS.FLOW_NAME).value = row.flowName;
      
      worksheetRow.commit();
    });

  }

  populateAgentStatus(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.AGENT_STATUS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.AGENT_STATUS}" not found in template`);
      return;
    }

    const mappedData = mapAgentStatus(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.AGENT_STATUS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.AGENT_STATUS.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.AGENT_STATUS.DESCRIPTION).value = row.description;
      
      worksheetRow.commit();
    });

  }

  populateQueues(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.QUEUES);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.QUEUES}" not found in template`);
      return;
    }

    const mappedData = mapQueues(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.QUEUES + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.HOURS_OF_OPERATION).value = row.hoursOfOperationName;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.OUTBOUND_CALLER_ID_NAME).value = row.outboundCallerIdName;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.OUTBOUND_CALLER_ID_NUMBER).value = row.outboundCallerNumber;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.OUTBOUND_WHISPER_FLOW_ID).value = row.outboundFlowName;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUEUES.MAX_CONTACTS).value = row.maxContacts;
      
      worksheetRow.commit();
    });

  }

  populateQuickConnects(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.QUICK_CONNECTS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.QUICK_CONNECTS}" not found in template`);
      return;
    }

    const mappedData = mapQuickConnects(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.QUICK_CONNECTS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.QUICK_CONNECTS.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUICK_CONNECTS.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUICK_CONNECTS.TYPE).value = row.type;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUICK_CONNECTS.DESTINATION).value = row.targetName;
      worksheetRow.getCell(COLUMN_MAPPINGS.QUICK_CONNECTS.CONTACT_FLOW_NAME).value = row.contactFlowName;
      
      worksheetRow.commit();
    });

  }

  populateRoutingProfiles(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.ROUTING_PROFILES);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.ROUTING_PROFILES}" not found in template`);
      return;
    }

    const mappedData = mapRoutingProfiles(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.ROUTING_PROFILES + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.CHANNELS).value = row.channels;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.CHAT_CONCURRENCY).value = row.chatConcurrency;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.TASK_CONCURRENCY).value = row.taskConcurrency;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.EMAIL_CONCURRENCY).value = row.emailConcurrency;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.DEFAULT_OUTBOUND_QUEUE).value = row.defaultOutboundQueue;
      worksheetRow.getCell(COLUMN_MAPPINGS.ROUTING_PROFILES.ASSOCIATED_QUEUES).value = row.associatedQueues;
      
      worksheetRow.commit();
    });

  }

  populateUserHierarchyData(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.USER_HIERARCHIES);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.USER_HIERARCHIES}" not found in template`);
      return;
    }

    const mappedData = mapUserHierarchyData(exportedData);
    
    // Populate hierarchy structure (row 5)
    if (mappedData.hierarchyStructure && mappedData.hierarchyStructure.length > 0) {
      const structureRow = worksheet.getRow(HIERARCHY_STRUCTURE_ROW);
      
      structureRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_ONE).value = mappedData.hierarchyStructure[0];
      structureRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_TWO).value = mappedData.hierarchyStructure[1];
      structureRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_THREE).value = mappedData.hierarchyStructure[2];
      structureRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_FOUR).value = mappedData.hierarchyStructure[3];
      structureRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_FIVE).value = mappedData.hierarchyStructure[4];
      
      structureRow.commit();
    }

    // Populate grouped hierarchy data (starting from row 6)
    if (mappedData.groupedHierarchy && mappedData.groupedHierarchy.length > 0) {
      mappedData.groupedHierarchy.forEach((row, index) => {
        const rowNum = DATA_START_ROW.USER_HIERARCHY_DATA + index;
        const worksheetRow = worksheet.getRow(rowNum);
        
        worksheetRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_ONE).value = row[0];
        worksheetRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_TWO).value = row[1];
        worksheetRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_THREE).value = row[2];
        worksheetRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_FOUR).value = row[3];
        worksheetRow.getCell(COLUMN_MAPPINGS.USER_HIERARCHY_DATA.LEVEL_FIVE).value = row[4];
        
        worksheetRow.commit();
      });
    }

  }

  populateUsers(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.USERS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.USERS}" not found in template`);
      return;
    }

    const mappedData = mapUsers(exportedData);
    
    if (mappedData.length === 0) {
      return;
    }

    mappedData.forEach((user, index) => {
      const rowNum = DATA_START_ROW.USERS + index;
      const row = worksheet.getRow(rowNum);
      
      row.getCell(COLUMN_MAPPINGS.USERS.FIRST_NAME).value = user.firstName;
      row.getCell(COLUMN_MAPPINGS.USERS.LAST_NAME).value = user.lastName;
      row.getCell(COLUMN_MAPPINGS.USERS.EMAIL_ADDRESS).value = user.emailAddress;
      row.getCell(COLUMN_MAPPINGS.USERS.USER_LOGIN).value = user.userLogin;
      row.getCell(COLUMN_MAPPINGS.USERS.ROUTING_PROFILE).value = user.routingProfile;
      row.getCell(COLUMN_MAPPINGS.USERS.SECURITY_PROFILE).value = user.securityProfile;
      row.getCell(COLUMN_MAPPINGS.USERS.PHONE_TYPE).value = user.phoneType;
      row.getCell(COLUMN_MAPPINGS.USERS.PHONE_NUMBER).value = user.phoneNumber;
      row.getCell(COLUMN_MAPPINGS.USERS.SOFT_PHONE_AUTO_ACCEPT).value = user.softPhoneAutoAccept;
      row.getCell(COLUMN_MAPPINGS.USERS.ACW_TIMEOUT).value = user.acwTimeout;
      row.getCell(COLUMN_MAPPINGS.USERS.LEVEL_1).value = user.level1;
      row.getCell(COLUMN_MAPPINGS.USERS.LEVEL_2).value = user.level2;
      row.getCell(COLUMN_MAPPINGS.USERS.LEVEL_3).value = user.level3;
      row.getCell(COLUMN_MAPPINGS.USERS.LEVEL_4).value = user.level4;
      row.getCell(COLUMN_MAPPINGS.USERS.LEVEL_5).value = user.level5;
      
      row.commit();
    });

  }

  populateSecurityProfiles(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.SECURITY_PROFILES);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.SECURITY_PROFILES}" not found in template`);
      return;
    }

    const mappedData = mapSecurityProfiles(exportedData);
    
    if (!mappedData.orderedProfiles || mappedData.orderedProfiles.length === 0) {
      return;
    }

    // Read existing permission names from column B (API Names) starting from row 8
    const permissions = [];
    let currentRow = DATA_START_ROW.SECURITY_PROFILES;
    
    // Read permissions from the worksheet until we hit an empty cell or end of data
    while (currentRow <= 1000) { // Set a reasonable upper limit
      const apiNameCell = worksheet.getCell(`${COLUMN_MAPPINGS.SECURITY_PROFILES.API_NAME}${currentRow}`);
      const uiNameCell = worksheet.getCell(`${COLUMN_MAPPINGS.SECURITY_PROFILES.UI_NAME}${currentRow}`);
      
      // Stop if both API name and UI name are empty
      if ((!apiNameCell.value || apiNameCell.value.toString().trim() === '') && 
          (!uiNameCell.value || uiNameCell.value.toString().trim() === '')) {
        break;
      }
      
      // Only process rows that have an API name (column B) AND a UI name (column A)
      // This ensures we skip merged section headers and only process actual permission rows
      if (apiNameCell.value && apiNameCell.value.toString().trim() !== '' &&
          uiNameCell.value && uiNameCell.value.toString().trim() !== '' &&
          !apiNameCell.isMerged) {
        permissions.push({
          apiName: apiNameCell.value.toString().trim(),
          rowNumber: currentRow
        });
      }
      
      currentRow++;
    }


    // Process each security profile
    mappedData.orderedProfiles.forEach((profile, profileIndex) => {
      // Determine which column to use
      let columnLetter;
      
      if (profileIndex < DEFAULT_SECURITY_PROFILES.length) {
        // Use fixed columns for default profiles
        const columnMappings = [
          COLUMN_MAPPINGS.SECURITY_PROFILES.ADMIN,
          COLUMN_MAPPINGS.SECURITY_PROFILES.AGENT,
          COLUMN_MAPPINGS.SECURITY_PROFILES.QUALITY_ANALYST,
          COLUMN_MAPPINGS.SECURITY_PROFILES.CALL_CENTER_MANAGER
        ];
        columnLetter = columnMappings[profileIndex];
      } else {
        // Use additional columns starting from H
        const additionalColumnIndex = profileIndex - DEFAULT_SECURITY_PROFILES.length;
        const startColumn = COLUMN_MAPPINGS.SECURITY_PROFILES.ADDITIONAL_PROFILES_START;
        const startColumnIndex = startColumn.charCodeAt(0) - 64; // Convert A=1, B=2, etc.
        columnLetter = getColumnLetter(startColumnIndex + additionalColumnIndex);
        
        // For additional profiles, populate the name in row 4 and description in row 5
        const nameRow = worksheet.getRow(4);
        const nameCell = nameRow.getCell(columnLetter);
        nameCell.value = profile.name;
        nameRow.commit();
        
        const descRow = worksheet.getRow(5);
        const descCell = descRow.getCell(columnLetter);
        descCell.value = profile.description;
        descRow.commit();
      }

      // Process each permission (only for actual permission rows)
      permissions.forEach(permission => {
        const cell = worksheet.getCell(`${columnLetter}${permission.rowNumber}`);
        const hasPermission = profile.permissions.includes(permission.apiName);
        
        if (hasPermission) {
          cell.value = 'Yes';
        } else {
          cell.value = 'No';
        }
      });
    });

  }

  async populateLambdaFunctions(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.LAMBDA_FUNCTIONS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.LAMBDA_FUNCTIONS}" not found in template`);
      return;
    }

    const mappedData = await mapLambdaFunctions(exportedData);
    
    if (mappedData.length === 0) {
      return;
    }

    // Get data options from first function (all should have the same options)
    const dataOptions = mappedData[0]._dataOptions || {
      includeRuntimeDeprecation: true,
      includeLogRetention: true,
      includeLastInvocation: true,
      includeInvocationCounts: true
    };

    // Update header row based on enabled options
    try {
      const headerRow = worksheet.getRow(3); // Assuming headers are in row 3
      
      // Always show basic columns
      headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.FUNCTION_NAME).value = 'Function Name';
      headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.DESCRIPTION).value = 'Description';
      headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.RUNTIME).value = 'Runtime';
      headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LAST_MODIFIED).value = 'Last Modified';
      
      // Conditionally show optional columns
      if (dataOptions.includeLogRetention) {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LOG_RETENTION).value = 'Log Retention';
      } else {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LOG_RETENTION).value = 'Log Retention (Disabled)';
      }
      
      if (dataOptions.includeLastInvocation) {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LAST_INVOCATION).value = 'Last Invocation';
      } else {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LAST_INVOCATION).value = 'Last Invocation (Disabled)';
      }
      
      if (dataOptions.includeRuntimeDeprecation) {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_CREATE).value = 'Block Function Create';
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_UPDATE).value = 'Block Function Update';
      } else {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_CREATE).value = 'Block Function Create (Disabled)';
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_UPDATE).value = 'Block Function Update (Disabled)';
      }
      
      if (dataOptions.includeInvocationCounts) {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.INVOCATIONS_30_DAYS).value = 'Invocations (30 days)';
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.ERRORS_30_DAYS).value = 'Errors (30 days)';
      } else {
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.INVOCATIONS_30_DAYS).value = 'Invocations (30 days) (Disabled)';
        headerRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.ERRORS_30_DAYS).value = 'Errors (30 days) (Disabled)';
      }
      
      headerRow.commit();
    } catch (error) {
      console.warn('Failed to update Lambda function headers:', error);
    }

    // Populate data rows
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.LAMBDA_FUNCTIONS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      // Always populate basic columns
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.FUNCTION_NAME).value = row.functionName;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.RUNTIME).value = row.runtime;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LAST_MODIFIED).value = row.lastModified;
      
      // Conditionally populate optional columns
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LOG_RETENTION).value = row.logRetention;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.LAST_INVOCATION).value = row.lastInvocation;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_CREATE).value = row.blockFunctionCreate;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.BLOCK_FUNCTION_UPDATE).value = row.blockFunctionUpdate;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.INVOCATIONS_30_DAYS).value = row.invocations30Days;
      worksheetRow.getCell(COLUMN_MAPPINGS.LAMBDA_FUNCTIONS.ERRORS_30_DAYS).value = row.errors30Days;
      
      worksheetRow.commit();
    });

    const enabledFeatures = [];
    if (dataOptions.includeRuntimeDeprecation) enabledFeatures.push('runtime deprecation');
    if (dataOptions.includeLogRetention) enabledFeatures.push('log retention');
    if (dataOptions.includeLastInvocation) enabledFeatures.push('last invocation');
    if (dataOptions.includeInvocationCounts) enabledFeatures.push('invocation counts');

  }

  populateLexBots(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.LEX_BOTS_V2);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.LEX_BOTS_V2}" not found in template`);
      return;
    }

    const mappedData = mapLexBots(exportedData);  // Use the updated function
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.LEX_BOTS_V2 + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.BOT_NAME).value = row.botName;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.SUBJECT_TO_COPPA).value = row.subjectToCoppa;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.IDLE_SESSION_TIMEOUT).value = row.idleSessionTimeout;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.LANGUAGES).value = row.languages;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ALIAS_VERSION).value = row.aliasVersion;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ASSOCIATED_TO_CONNECT).value = row.associatedToConnect;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.INTENT_COUNT).value = row.intentCount;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.SLOT_TYPE_COUNT).value = row.slotTypeCount;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.LEX_VERSION).value = row.lexVersion;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.CONVERSATION_METRICS).value = row.conversationMetrics;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.UTTERANCE_METRICS).value = row.utteranceMetrics;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ASSOCIATED_FLOWS).value = row.associatedFlows;
      
      // Enable text wrapping for multi-line columns
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ALIAS_VERSION).alignment = { 
        wrapText: true, 
        vertical: 'top' 
      };
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ASSOCIATED_TO_CONNECT).alignment = { 
        wrapText: true, 
        vertical: 'top' 
      };
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_BOTS_V2.ASSOCIATED_FLOWS).alignment = { 
        wrapText: true, 
        vertical: 'top' 
      };
      
      worksheetRow.commit();
    });

  }

// Keep the old function name for backward compatibility
  populateLexBotsV2(exportedData) {
    return this.populateLexBots(exportedData);
  }

  populateLexIntents(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.LEX_INTENTS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.LEX_INTENTS}" not found in template`);
      return;
    }

    const mappedData = mapLexIntents(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.LEX_INTENTS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.BOT_NAME).value = row.botName;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.LOCALE_ID).value = row.localeId;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.INTENT_NAME).value = row.intentName;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.SAMPLE_UTTERANCES).value = row.sampleUtterances;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.CONFIRMATION_SETTING).value = row.confirmationSetting;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.SLOT_COUNT).value = row.slotCount;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.FULFILLMENT_TYPE).value = row.fulfillmentType;
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.PARENT_INTENT_SIGNATURE).value = row.parentIntentSignature;
      
      // Enable text wrapping for multi-line columns
      worksheetRow.getCell(COLUMN_MAPPINGS.LEX_INTENTS.SAMPLE_UTTERANCES).alignment = { 
        wrapText: true, 
        vertical: 'top' 
      };
      
      worksheetRow.commit();
    });

  }

  populateLexSlotsAndTypes(exportedData) {
  if (!this.workbook) {
    throw new Error('Excel exporter not initialized');
  }

  const worksheet = this.workbook.getWorksheet(SHEET_NAMES.LEX_SLOTS_AND_TYPES);
  if (!worksheet) {
    console.warn(`Sheet "${SHEET_NAMES.LEX_SLOTS_AND_TYPES}" not found in template`);
    return;
  }

  const mappedData = mapLexSlotsAndTypes(exportedData);
  
  mappedData.forEach((row, index) => {
    const rowNum = DATA_START_ROW.LEX_SLOTS_AND_TYPES + index;
    const worksheetRow = worksheet.getRow(rowNum);
    
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.BOT_NAME).value = row.botName;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.LOCALE_ID).value = row.localeId;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.INTENT_NAME).value = row.intentName;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_NAME).value = row.slotName;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_TYPE_ID).value = row.slotTypeId;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_TYPE_NAME).value = row.slotTypeName;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_DESCRIPTION).value = row.slotDescription;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_TYPE_DESCRIPTION).value = row.slotTypeDescription;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_TYPE_VALUES).value = row.slotTypeValues;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.VALUE_SELECTION_STRATEGY).value = row.valueSelectionStrategy;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.VALUE_ELICITATION_SETTING).value = row.valueElicitationSetting;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.REQUIRED).value = row.required;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.PARENT_SLOT_TYPE_SIGNATURE).value = row.parentSlotTypeSignature;
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.EXTERNAL_SOURCE_SETTING).value = row.externalSourceSetting;
    
    // Enable text wrapping for multi-line columns
    worksheetRow.getCell(COLUMN_MAPPINGS.LEX_SLOTS_AND_TYPES.SLOT_TYPE_VALUES).alignment = { 
      wrapText: true, 
      vertical: 'top' 
    };
    
    worksheetRow.commit();
  });

}

  populateConnectLambdaFunctions(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.CONNECT_LAMBDA);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.CONNECT_LAMBDA}" not found in template`);
      return;
    }

    const mappedData = mapConnectLambdaFunctions(exportedData);
    
    mappedData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.CONNECT_LAMBDA + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.CONNECT_LAMBDA.FUNCTION_NAME).value = row.functionName;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONNECT_LAMBDA.DESCRIPTION).value = row.description;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONNECT_LAMBDA.ASSOCIATED_FLOWS).value = row.associatedFlows;
      
      worksheetRow.commit();
    });

  }

  populateContactFlows(exportedData) {
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.CONTACT_FLOWS);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.CONTACT_FLOWS}" not found in template`);
      return;
    }

    // Get mapped data for both flows and modules
    const mappedFlows = mapContactFlows(exportedData);
    const mappedModules = mapContactFlowModules(exportedData);
    
    // Combine flows and modules data
    const allFlowData = [...mappedFlows, ...mappedModules];
    
    allFlowData.forEach((row, index) => {
      const rowNum = DATA_START_ROW.CONTACT_FLOWS + index;
      const worksheetRow = worksheet.getRow(rowNum);
      
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.NAME).value = row.name;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.STATE).value = row.state;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.STATUS).value = row.status;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.TYPE).value = row.type;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.IS_DEFAULT).value = row.isDefault;
      worksheetRow.getCell(COLUMN_MAPPINGS.CONTACT_FLOWS.CONTACT_FLOW_ERRORS).value = row.contactFlowErrors || 0;
      
      worksheetRow.commit();
    });

  }

  populateInstanceConfiguration(exportedData) {
  if (!this.workbook) {
    throw new Error('Excel exporter not initialized');
  }

  const worksheet = this.workbook.getWorksheet(SHEET_NAMES.INSTANCE_CONFIG);
  if (!worksheet) {
    console.warn(`Sheet "${SHEET_NAMES.INSTANCE_CONFIG}" not found in template`);
    return;
  }

  const mappedData = mapInstanceConfiguration(exportedData);
  
  if (mappedData.length === 0) {
    console.log('No instance configuration data to populate');
    return;
  }

  // Use the first instance's data (since it's one instance per workbook)
  const instanceData = mappedData[0];

  // Populate the worksheet using the specific row mappings from the screenshot
  INSTANCE_CONFIG_FIELDS.forEach((field) => {
    const worksheetRow = worksheet.getRow(field.row);
    
    // Column B: Field value (Column A already has labels in the template)
    worksheetRow.getCell(COLUMN_MAPPINGS.INSTANCE_CONFIG.VALUE).value = instanceData[field.key] || 'N/A';
    
    worksheetRow.commit();
  });

  console.log(`Populated Instance Configuration with ${INSTANCE_CONFIG_FIELDS.length} fields`);
}

  populateTrustedAdvisorRecommendations(exportedData) {
    
    if (!this.workbook) {
      throw new Error('Excel exporter not initialized');
    }

    const worksheet = this.workbook.getWorksheet(SHEET_NAMES.TRUSTED_ADVISOR);
    if (!worksheet) {
      console.warn(`Sheet "${SHEET_NAMES.TRUSTED_ADVISOR}" not found in template`);
      return;
    }

    // Extract recommendations data from the first instance (same data for all instances)
    const firstInstanceId = Object.keys(exportedData)[0];
    const instanceData = exportedData[firstInstanceId];
    const allRecommendations = instanceData?.recommendations || [];
    
    if (!Array.isArray(allRecommendations) || allRecommendations.length === 0) {
      return;
    }

    // Filter to only include problematic recommendations (Investigation Recommended or Action Recommended)
    const recommendations = allRecommendations.filter(rec => 
      rec.Status === 'Investigation Recommended' || rec.Status === 'Action Recommended'
    );
    
    if (recommendations.length === 0) {
      return;
    }

    const startRow = DATA_START_ROW.TRUSTED_ADVISOR;
    let currentRow = startRow;

    recommendations.forEach((recommendation) => {
      const row = worksheet.getRow(currentRow);
      
      // Populate each column according to our updated COLUMN_MAPPINGS (no description or type columns)
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.RECOMMENDATION_NAME).value = recommendation.Name || '';
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.CATEGORY).value = recommendation.Category || '';
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.STATUS).value = recommendation.Status || '';
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.POTENTIAL_MONTHLY_SAVINGS).value = recommendation.PotentialMonthlySavings || '';
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.RESOURCES_FLAGGED).value = recommendation.ResourcesFlagged || 0;
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.RESOURCES_IGNORED).value = recommendation.ResourcesIgnored || 0;
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.RESOURCES_SUPPRESSED).value = recommendation.ResourcesSuppressed || 0;
      row.getCell(COLUMN_MAPPINGS.TRUSTED_ADVISOR.RESOURCES_PROCESSED).value = recommendation.ResourcesProcessed || 0;
      
      row.commit();
      currentRow++;
    });

  }

  // Updated the export method to handle async Lambda population
  async export(exportedData, filename = 'AWS_Connect_Export.xlsx') {
    try {
      // Always reinitialize workbook for each export to ensure clean state
      await this.initialize();

      // Populate all available resource types
      if (exportedData && Object.keys(exportedData).length > 0) {
        this.populateInstanceConfiguration(exportedData);
        this.populateHoursOfOperation(exportedData);
        this.populateDialedNumberMappings(exportedData);
        this.populateAgentStatus(exportedData);
        this.populateQueues(exportedData);
        this.populateQuickConnects(exportedData);
        this.populateRoutingProfiles(exportedData);
        this.populateUserHierarchyData(exportedData);
        this.populateUsers(exportedData);
        this.populateSecurityProfiles(exportedData);
        await this.populateLambdaFunctions(exportedData);
        this.populateLexBots(exportedData);
        this.populateLexIntents(exportedData);
        this.populateLexSlotsAndTypes(exportedData);
        this.populateConnectLambdaFunctions(exportedData);
        this.populateContactFlows(exportedData);
        this.populateTrustedAdvisorRecommendations(exportedData);
      }

      // Generate the Excel file as a buffer
      const buffer = await this.workbook.xlsx.writeBuffer();

      // Create blob and trigger download
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Excel export failed:', error);
      throw error;
    }
  }

  // Method to add other resource types in the future
  async populateResourceType(resourceType, exportedData) {
    switch (resourceType) {
      case 'hoursOfOperation':
        this.populateHoursOfOperation(exportedData);
        break;
      case 'phoneNumbers':
      case 'dialedNumberMappings':
        this.populateDialedNumberMappings(exportedData);
        break;
      case 'agentStatuses':
        this.populateAgentStatus(exportedData);
        break;
      case 'queues':
        this.populateQueues(exportedData);
        break;
      case 'quickConnects':
        this.populateQuickConnects(exportedData);
        break;
      case 'routingProfiles':
        this.populateRoutingProfiles(exportedData);
        break;
      case 'userHierarchyGroups':
        this.populateUserHierarchyData(exportedData);
        break;
      case 'users':
        this.populateUsers(exportedData);
        break;
      case 'securityProfiles':
        this.populateSecurityProfiles(exportedData);
        break;
      case 'functions': // Updated to async
        await this.populateLambdaFunctions(exportedData);
        break;
      case 'contactFlows':
        this.populateContactFlows(exportedData);
        break;
      case 'contactFlowModules':
        this.populateContactFlows(exportedData);
        break;
      case 'lexBotV2':
        this.populateLexBots(exportedData);
        if (exportedData && Object.values(exportedData).some(instanceData => instanceData.lexBotV2)) {
          this.populateLexIntents(exportedData);
          this.populateLexSlotsAndTypes(exportedData);
        }
        break;
      case 'recommendations':
        this.populateTrustedAdvisorRecommendations(exportedData);
        break;
      default:
        console.warn(`Unknown resource type: ${resourceType}`);
    }
  }

  // Utility method to get worksheet by name
  getWorksheet(name) {
    return this.workbook?.getWorksheet(name);
  }

  // Utility method to check if template is loaded
  isInitialized() {
    return this.workbook !== null;
  }
}